import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

// Import Shepherd.js dynamically
let Shepherd: any;

@Injectable({
  providedIn: 'root'
})
export class OnboardingService {
  private tour: any | null = null;
  private readonly TOUR_COMPLETED_KEY = 'anchored_growth_tour_completed';
  private readonly TOUR_SKIPPED_KEY = 'anchored_growth_tour_skipped';
  private readonly DONT_SHOW_AGAIN_KEY = 'anchored_growth_tour_dont_show';

  constructor(private router: Router) {
    // Load Shepherd.js dynamically
    this.loadShepherd();
  }

  private async loadShepherd(): Promise<void> {
    if (!Shepherd) {
      try {
        const shepherdModule = await import('shepherd.js');
        Shepherd = shepherdModule.default || shepherdModule;
      } catch (error) {
        console.error('Failed to load Shepherd.js:', error);
      }
    }
  }

  /**
   * Check if the tour should be shown to the user
   */
  shouldShowTour(): boolean {
    // Don't show if user chose "don't show again"
    if (localStorage.getItem(this.DONT_SHOW_AGAIN_KEY) === 'true') {
      return false;
    }

    // Don't show if user already completed or skipped the tour
    const completed = localStorage.getItem(this.TOUR_COMPLETED_KEY) === 'true';
    const skipped = localStorage.getItem(this.TOUR_SKIPPED_KEY) === 'true';

    return !completed && !skipped;
  }

  /**
   * Initialize and start the company setup tour
   */
  async startCompanySetupTour(): Promise<void> {
    if (!this.shouldShowTour()) {
      return;
    }

    await this.createTour();

    // Small delay to ensure DOM is ready
    setTimeout(() => {
      this.tour?.start();
    }, 500);
  }

  /**
   * Start the tour manually (from help menu or onboarding button)
   */
  async startTourManually(): Promise<void> {
    await this.createTour();
    setTimeout(() => {
      this.tour?.start();
    }, 100);
  }

  /**
   * Create the Shepherd tour with all steps
   */
  private async createTour(): Promise<void> {
    // Clean up existing tour properly
    if (this.tour) {
      try {
        // Remove event listeners before destroying
        this.tour.off('complete');
        this.tour.off('cancel');
        this.tour.cancel();
        this.tour = null;
      } catch (error) {
        console.warn('Error cleaning up existing tour:', error);
        this.tour = null;
      }
    }

    // Ensure Shepherd is loaded
    await this.loadShepherd();

    if (!Shepherd) {
      console.error('Shepherd.js not available');
      return;
    }

    this.tour = new Shepherd.Tour({
      useModalOverlay: true,
      defaultStepOptions: {
        classes: 'anchored-growth-tour-step',
        scrollTo: { behavior: 'smooth', block: 'center' },
        canClickTarget: false,
        cancelIcon: {
          enabled: true,
        },
        when: {
          show: () => {
            // Add tour-active class to body for custom styling
            document.body.classList.add('tour-active');
          },
          hide: () => {
            document.body.classList.remove('tour-active');
          }
        }
      }
    } as any);

    this.addTourSteps();
    this.setupTourEvents();
  }

  /**
   * Add simplified tour steps - focused on getting to integrations
   */
  private addTourSteps(): void {
    if (!this.tour) return;

    // Step 1: Welcome
    this.tour.addStep({
      title: 'Welcome to Anchored Growth!',
      text: `
        <div class="tour-content">
          <p class="tour-subtitle">Your sales intelligence platform</p>
          <p>Let's take a quick look around so you can start analyzing your outreach right away.</p>
          <div class="tour-tip">
            <strong>Your 14-day Pro trial is active!</strong> Full access to all features.
          </div>
        </div>
      `,
      buttons: [
        {
          text: 'Skip Tour',
          classes: 'btn btn-skip',
          action: () => this.skipTour()
        },
        {
          text: "Show me around",
          classes: 'btn btn-primary',
          action: () => this.tour?.next()
        }
      ]
    });

    // Step 2: Dashboard Overview
    this.tour.addStep({
      title: 'Your Dashboard',
      text: `
        <div class="tour-content">
          <p>This is your command center. Once you connect integrations and import campaigns, you'll see:</p>
          <ul class="tour-list">
            <li>Real-time response metrics</li>
            <li>Weekly activity trends</li>
            <li>AI-powered message insights</li>
            <li>A/B test performance</li>
          </ul>
        </div>
      `,
      attachTo: {
        element: 'a[routerLink="/dashboard"]',
        on: 'right'
      },
      buttons: [
        {
          text: 'Skip Tour',
          classes: 'btn btn-skip',
          action: () => this.skipTour()
        },
        {
          text: 'Next',
          classes: 'btn btn-primary',
          action: () => this.tour?.next()
        }
      ]
    });

    // Step 3: Integrations (KEY STEP)
    this.tour.addStep({
      title: 'Connect Your Tools',
      text: `
        <div class="tour-content">
          <p><strong>This is where the magic starts!</strong></p>
          <p>Connect your sales tools to pull in your outreach data:</p>
          <ul class="tour-list">
            <li><strong>Instantly / SmartLead:</strong> Email campaigns</li>
            <li><strong>LinkedIn:</strong> Social outreach</li>
            <li><strong>More coming soon!</strong></li>
          </ul>
          <div class="tour-tip" style="background: #fef3c7; border-left-color: #f59e0b;">
            <strong>Start here:</strong> Connect your first integration to begin seeing your data!
          </div>
        </div>
      `,
      attachTo: {
        element: 'a[routerLink="/integrations"]',
        on: 'right'
      },
      buttons: [
        {
          text: 'Skip Tour',
          classes: 'btn btn-skip',
          action: () => this.skipTour()
        },
        {
          text: 'Next',
          classes: 'btn btn-primary',
          action: () => this.tour?.next()
        }
      ]
    });

    // Step 4: Final - Go to Integrations
    this.tour.addStep({
      title: 'Ready to Get Started!',
      text: `
        <div class="tour-content">
          <p class="tour-subtitle">Let's connect your first integration</p>
          <p>Click the button below to set up your first connection and start analyzing your outreach performance.</p>
          <div class="tour-final-tip">
            <p>Need help? Look for the <strong>?</strong> icon or contact support anytime.</p>
          </div>
        </div>
      `,
      buttons: [
        {
          text: "Don't show again",
          classes: 'btn btn-ghost',
          action: () => this.completeTour(true)
        },
        {
          text: 'Connect Integration',
          classes: 'btn btn-primary btn-large',
          action: () => this.completeTourAndNavigate()
        }
      ]
    });
  }

  /**
   * Setup tour event handlers
   */
  private setupTourEvents(): void {
    if (!this.tour) return;

    // Use arrow functions and bind context to avoid circular references
    const handleComplete = () => {
      document.body.classList.remove('tour-active');
      this.completeTour();
    };

    const handleCancel = () => {
      document.body.classList.remove('tour-active');
      this.skipTour();
    };

    this.tour.on('complete', handleComplete);
    this.tour.on('cancel', handleCancel);
  }

  /**
   * Complete the tour and optionally set "don't show again"
   */
  private completeTour(dontShowAgain: boolean = false): void {
    localStorage.setItem(this.TOUR_COMPLETED_KEY, 'true');

    if (dontShowAgain) {
      localStorage.setItem(this.DONT_SHOW_AGAIN_KEY, 'true');
    }

    // Clean up tour and close modal
    if (this.tour) {
      try {
        // Remove event listeners to prevent recursion
        this.tour.off('complete');
        this.tour.off('cancel');
        // Hide current step to close the modal
        this.tour.hide();
        // Cancel the tour to fully close it
        this.tour.cancel();
      } catch (error) {
        console.warn('Error during tour cleanup:', error);
      } finally {
        this.tour = null;
        // Ensure body class is removed
        document.body.classList.remove('tour-active');
      }
    }

    console.log('Onboarding tour completed');
  }

  /**
   * Skip the tour
   */
  private skipTour(): void {
    localStorage.setItem(this.TOUR_SKIPPED_KEY, 'true');

    // Clean up tour and close modal
    if (this.tour) {
      try {
        // Remove event listeners to prevent recursion
        this.tour.off('complete');
        this.tour.off('cancel');
        // Hide current step to close the modal
        this.tour.hide();
        // Cancel the tour to fully close it
        this.tour.cancel();
      } catch (error) {
        console.warn('Error during tour cleanup:', error);
      } finally {
        this.tour = null;
        // Ensure body class is removed
        document.body.classList.remove('tour-active');
      }
    }

    console.log('Onboarding tour skipped');
  }

  /**
   * Complete tour and navigate to integrations page (the key conversion point)
   */
  private completeTourAndNavigate(): void {
    this.completeTour();
    this.router.navigate(['/integrations'], { queryParamsHandling: 'merge' });
  }

  /**
   * Reset tour state (for testing or user request)
   */
  resetTourState(): void {
    localStorage.removeItem(this.TOUR_COMPLETED_KEY);
    localStorage.removeItem(this.TOUR_SKIPPED_KEY);
    localStorage.removeItem(this.DONT_SHOW_AGAIN_KEY);
    console.log('Tour state reset');
  }

  /**
   * Check if tour has been completed
   */
  isTourCompleted(): boolean {
    return localStorage.getItem(this.TOUR_COMPLETED_KEY) === 'true';
  }

  /**
   * Check if user chose to never show tour again
   */
  isDontShowAgainSet(): boolean {
    return localStorage.getItem(this.DONT_SHOW_AGAIN_KEY) === 'true';
  }
}
